/**
 * useChatPrefs — tiny localStorage-backed store for chat-view
 * preferences that don't warrant a backend round-trip.
 *
 * Currently exposes ONE flag:
 *   `showSources` — when true, chat renders the "Sources" strip
 *   (external tag-links to Tavily / TMDB / Wikipedia sources) under
 *   every reply. Defaults OFF because most users find the strip
 *   noisy — the compact confidence pill stays visible either way.
 *
 * Design pledge
 * -------------
 * Every getter reads localStorage synchronously so components can
 * render the correct state on first paint (no flash). Every setter
 * dispatches a ``storage`` event on the same tab so subscribers hear
 * changes without prop-drilling.
 */
import { useEffect, useState, useCallback } from "react";

const SOURCES_KEY = "rammy_show_sources";
const CONFIDENCE_KEY = "rammy_show_confidence";

function _read() {
  try { return localStorage.getItem(SOURCES_KEY) === "1"; }
  catch { return false; }
}

function _readConfidence() {
  try {
    const v = localStorage.getItem(CONFIDENCE_KEY);
    // Default ON when unset — small pill, trust-building.
    return v === null ? true : v === "1";
  } catch { return true; }
}

function _write(on) {
  try {
    if (on) localStorage.setItem(SOURCES_KEY, "1");
    else localStorage.removeItem(SOURCES_KEY);
    // Fire a same-tab notification so other subscribers refresh too.
    window.dispatchEvent(new StorageEvent("storage", { key: SOURCES_KEY }));
  } catch { /* no-op */ }
}

export function useShowSources() {
  const [value, setValue] = useState(_read);
  useEffect(() => {
    const onStorage = (e) => {
      if (!e.key || e.key === SOURCES_KEY) setValue(_read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const set = useCallback((on) => { _write(!!on); setValue(!!on); }, []);
  return [value, set];
}

export function useShowConfidence() {
  const [value, setValue] = useState(_readConfidence);
  useEffect(() => {
    const onStorage = (e) => {
      if (!e.key || e.key === CONFIDENCE_KEY) setValue(_readConfidence());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const set = useCallback((on) => {
    try {
      // Explicit "0" on off so first-time users still get the default-on
      // behaviour above; existing users who opt out land with "0".
      if (on) localStorage.setItem(CONFIDENCE_KEY, "1");
      else localStorage.setItem(CONFIDENCE_KEY, "0");
      window.dispatchEvent(new StorageEvent("storage", { key: CONFIDENCE_KEY }));
    } catch { /* no-op */ }
    setValue(!!on);
  }, []);
  return [value, set];
}

export function readShowSources() { return _read(); }
export function readShowConfidence() { return _readConfidence(); }
